<script lang="ts" setup>
import { AppButton, AppPassword } from '#core/components';
import type { FormErrors } from '#core/types/Form';
import { useVuelidate } from '@vuelidate/core';
import { computed, ref } from 'vue';
import type { ForgotPasswordResetPasswordForm } from '../../types';

const { username = '', token = '' } = defineProps<{
  username: string;
  token: string;
}>();

const { validations } = useValidation();

const showPassword = ref(false);
const showPasswordConfirmation = ref(false);

const router = useRouter();
const {showSnackbar} = useSnackbar();

const form = ref({
  password: '',
  password_confirmation: '',
});

const rules = computed(() => ({
  password: {
    required: validations.required('Password'),
    minLength: validations.minLength('Password', 8),
    password: validations.password(),
  },
  password_confirmation: {
    required: validations.required('Password Confirmation'),
    sameAs: validations.confirm('Passwords', form.value.password),
  },
}));

const v$ = useVuelidate(rules, form, {$autoDirty: true});

const {
  pending: loadingResetPassword,
  execute: execResetPassword,
  error: serverError,
} = useSubmit<{}, { data: FormErrors<ForgotPasswordResetPasswordForm> }>();

const handleSignUp = async (): Promise<void> => {
  const result = await v$.value.$validate();
  if (!result) return;

  await execResetPassword('/auth/reset-password', {
    method: 'POST',
    body: { ...form.value, username, token },
  });

  if (!serverError.value) {
    showSnackbar({
      text: 'Your password has been reset successfully',
      state: true,
    })
    await router.push({name: 'login'});
  }
};

const errors = computed(() => {
  return $parsedError(form.value, v$, serverError.value?.data?.errors || {});
});
</script>

<template>
  <v-form @submit.prevent="handleSignUp">
    <AppPassword
      v-model="form.password"
      label="Password"
      :disabled="loadingResetPassword"
      :error-messages="errors.password"
      data-testid="password"
      data-cy="input-password"
      autofocus
      class="mb-1"
    >
    </AppPassword>

    <AppPassword
      v-model="form.password_confirmation"
      label="Confirm Password"
      :disabled="loadingResetPassword"
      :error-messages="errors.password_confirmation"
      data-testid="password-confirmation"
      data-cy="input-password-confirmation"
    >
    </AppPassword>

    <AppButton data-testid="continue" block class="mt-4 font-weight-bold w-full" :loading="loadingResetPassword">
      Confirm
    </AppButton>
  </v-form>
</template>

<style lang="scss" scoped>
.v-card {
  margin-top: 132px;
}
</style>
